import React, { useState } from "react";
import {
  TextField,
  Button,
  Avatar,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import "./register.css";

const validationSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
  roleid: Yup.string().required("Role ID is required"),
  fullname: Yup.string().required("Full Name is required"),
  phonenumber: Yup.string().required("Phone Number is required"),
  passportnumber: Yup.string().required("Passport Number is required"),
});

// Common Input Styles
const inputStyle = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    marginBottom: "5px",
    width: "100%",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "15px",
  },
};

const Register = () => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      let formattedDateOfBirth = null;
      if (values.dateofbirth) {
        const parsedDate = new Date(values.dateofbirth);
        if (!isNaN(parsedDate.getTime())) {
          formattedDateOfBirth = parsedDate.toISOString().split("T")[0]; // Format to 'YYYY-MM-DD'
        } else {
          throw new Error("Invalid date of birth");
        }
      }

      const formData = new FormData();
      formData.append("username", values.username);
      formData.append("email", values.email);
      formData.append("password", values.password);
      formData.append("roleid", values.roleid);
      formData.append("fullname", values.fullname);
      formData.append("phonenumber", values.phonenumber);
      formData.append("passportnumber", values.passportnumber);
      formData.append("isactive", values.isactive ? 1 : 0);
      formData.append("isauthorized", values.isauthorized ? 1 : 0);

      // check child
      if (parseInt(values.roleid) === 1) {
        formData.append("childname", values.childname);
        formData.append("dateofbirth", formattedDateOfBirth);
        formData.append("medicalhistory", values.medicalhistory);
        formData.append("healthprofile", values.healthprofile);
        formData.append("healthpassport", values.healthpassport);
      }

      // check home manager & line manager
      if (parseInt(values.roleid) === 2 || parseInt(values.roleid) === 3) {
        formData.append("homename", values.homename);
      }

      // check care taker
      if (parseInt(values.roleid) === 4) {
        formData.append("homename", values.homename);
        formData.append("childid", values.childid);
        formData.append("scheduled", values.scheduled);
      }

      // check admin
      if (parseInt(values.roleid) === 5) {
        formData.append("performancerecord", values.performancerecord);
      }

      // Check if avatar file exists and append it to the form data
      if (image) {
        formData.append("image", image);
      } else {
        alert("Please upload an avatar image");
        setSubmitting(false);
        return; // Stop the submission if no avatar is present
      }

      // API call
      await axios.post("api/auth/register", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Registration successful!");
      resetForm();
      setImage(null);
      setImagePreview(null); // Reset the image preview
    } catch (error) {
      if (error.response) {
        console.error("Server responded with error:", error.response.data);
        alert(`Registration failed: ${error.response.data.message}`);
      } else if (error.request) {
        console.error("No response received from server:", error.request);
        alert("Registration failed: No response from server");
      } else {
        console.error("Error setting up request:", error.message);
        alert(`Registration failed: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle avatar image change
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file); // Store the actual file for upload
      setImagePreview(URL.createObjectURL(file)); // For previewing the image

      // Clean up the URL when no longer needed
      return () => URL.revokeObjectURL(file);
    } else {
      alert("Please upload a valid image file");
    }
  };

  return (
    <Grid className='register-container'>
      <Formik
        initialValues={{
          username: "",
          email: "",
          password: "",
          roleid: "",
          fullname: "",
          phonenumber: "",
          passportnumber: "",
          isactive: false,
          isauthorized: false,
          image: "",
          childname: "",
          dateofbirth: null,
          medicalhistory: "",
          healthprofile: "",
          healthpassport: "",
          homename: "",
          childid: "",
          scheduled: "",
          performancerecord: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
          <Form className='register-form' onSubmit={handleSubmit}>
            <Grid item xs={12} sm={8} md={4}>
              {/* <img src='/images/logo.png' alt='Active Care Logo' /> */}

              <Typography variant='h4' align='center' gutterBottom>
                Register Form
              </Typography>
              <Typography variant='body1' align='center' gutterBottom>
                Register to Access my personalized dashboard
                <br />
                and perform tasks within the system
              </Typography>
              {/* Avatar Upload */}
              <Grid item xs={12} mb={2}>
                {imagePreview && (
                  <Box mt={2} ml={20}>
                    <Avatar src={imagePreview} sx={{ width: 100, height: 100 }} />
                  </Box>
                )}
                <Button variant='contained' component='label'>
                  Upload Avatar
                  <input type='file' hidden accept='image/*' onChange={handleAvatarChange} />
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name='username'
                  variant='outlined'
                  placeholder='Enter your username'
                  value={values.username}
                  onChange={handleChange}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name='email'
                  type='email'
                  variant='outlined'
                  placeholder='Enter your email'
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant='outlined'
                  placeholder='Enter your password'
                  name='password'
                  type='password'
                  value={values.password}
                  onChange={handleChange}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant='outlined'
                  placeholder='Enter your fullname'
                  name='fullname'
                  value={values.fullname}
                  onChange={handleChange}
                  error={touched.fullname && Boolean(errors.fullname)}
                  helperText={touched.fullname && errors.fullname}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant='outlined'
                  placeholder='Enter your phonenumber'
                  name='phonenumber'
                  value={values.phonenumber}
                  onChange={handleChange}
                  error={touched.phonenumber && Boolean(errors.phonenumber)}
                  helperText={touched.phonenumber && errors.phonenumber}
                  sx={inputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant='outlined'
                  placeholder='Enter your passportnumber'
                  name='passportnumber'
                  value={values.passportnumber}
                  onChange={handleChange}
                  error={touched.passportnumber && Boolean(errors.passportnumber)}
                  helperText={touched.passportnumber && errors.passportnumber}
                  sx={inputStyle}
                />
              </Grid>

              <Grid container>
                <Grid>
                  <ToggleButtonGroup
                    value={values.isactive ? "active" : "inactive"}
                    exclusive
                    onChange={(event, value) => setFieldValue("isactive", value === "active")}
                  >
                    <ToggleButton value='active' aria-label='active'>
                      Active
                    </ToggleButton>
                    <ToggleButton value='inactive' aria-label='inactive'>
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid ml={2}>
                  <ToggleButtonGroup
                    value={values.isauthorized ? "authorized" : "unauthorized"}
                    exclusive
                    onChange={(event, value) => setFieldValue("isauthorized", value === "authorized")}
                  >
                    <ToggleButton value='authorized' aria-label='authorized'>
                      Authorized
                    </ToggleButton>
                    <ToggleButton value='unauthorized' aria-label='unauthorized'>
                      Unauthorized
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select name='roleid' value={values.roleid} onChange={handleChange} required>
                    <MenuItem value='0'>User</MenuItem>
                    <MenuItem value='1'>Child</MenuItem>
                    <MenuItem value='2'>Home Manager</MenuItem>
                    <MenuItem value='3'>Line Manager</MenuItem>
                    <MenuItem value='4'>Care Taker</MenuItem>
                    <MenuItem value='5'>Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {parseInt(values.roleid) === 1 && (
                <Grid className='hide-form'>
                  {/* Child Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your childname'
                      name='childname'
                      value={values.childname}
                      onChange={handleChange}
                      error={touched.childname && Boolean(errors.childname)}
                      helperText={touched.childname && errors.childname}
                      sx={inputStyle}
                    />
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your dateofbirth'
                      name='dateofbirth'
                      type='date'
                      value={values.dateofbirth}
                      onChange={handleChange}
                      error={touched.dateofbirth && Boolean(errors.dateofbirth)}
                      helperText={touched.dateofbirth && errors.dateofbirth}
                      InputLabelProps={{
                        shrink: true, // Keeps the label in place when the input is focused
                      }}
                      sx={inputStyle}
                    />
                  </Grid>

                  {/* Medical History */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your medicalhistory'
                      name='medicalhistory'
                      value={values.medicalhistory}
                      onChange={handleChange}
                      error={touched.medicalhistory && Boolean(errors.medicalhistory)}
                      helperText={touched.medicalhistory && errors.medicalhistory}
                      sx={inputStyle}
                    />
                  </Grid>

                  {/* Health Profile */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your healthprofile'
                      name='healthprofile'
                      value={values.healthprofile}
                      onChange={handleChange}
                      error={touched.healthprofile && Boolean(errors.healthprofile)}
                      helperText={touched.healthprofile && errors.healthprofile}
                      sx={inputStyle}
                    />
                  </Grid>

                  {/* Health Passport */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your healthpassport'
                      name='healthpassport'
                      value={values.healthpassport}
                      onChange={handleChange}
                      error={touched.healthpassport && Boolean(errors.healthpassport)}
                      helperText={touched.healthpassport && errors.healthpassport}
                      sx={inputStyle}
                    />
                  </Grid>
                </Grid>
              )}

              {(parseInt(values.roleid) === 2 || parseInt(values.roleid) === 3) && (
                <Grid className='hide-form'>
                  {/* Home Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your homename'
                      name='homename'
                      value={values.homename}
                      onChange={handleChange}
                      error={touched.homename && Boolean(errors.homename)}
                      helperText={touched.homename && errors.homename}
                      sx={inputStyle}
                    />
                  </Grid>
                </Grid>
              )}

              {parseInt(values.roleid) === 4 && (
                <Grid className='hide-form'>
                  {/* Home Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your homename'
                      name='homename'
                      value={values.homename}
                      onChange={handleChange}
                      error={touched.homename && Boolean(errors.homename)}
                      helperText={touched.homename && errors.homename}
                      sx={inputStyle}
                    />
                  </Grid>
                  {/* Child ID */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your childid'
                      name='childid'
                      value={values.childid}
                      onChange={handleChange}
                      error={touched.childid && Boolean(errors.childid)}
                      helperText={touched.childid && errors.childid}
                      sx={inputStyle}
                    />
                  </Grid>
                  {/* Schedule */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your scheduled'
                      name='scheduled'
                      value={values.scheduled}
                      onChange={handleChange}
                      error={touched.scheduled && Boolean(errors.scheduled)}
                      helperText={touched.scheduled && errors.scheduled}
                      sx={inputStyle}
                    />
                  </Grid>
                </Grid>
              )}

              {parseInt(values.roleid) === 5 && (
                <Grid className='hide-form'>
                  {/* Performance Record */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      placeholder='Enter your performancerecord'
                      name='performancerecord'
                      value={values.performancerecord}
                      onChange={handleChange}
                      error={touched.performancerecord && Boolean(errors.performancerecord)}
                      helperText={touched.performancerecord && errors.performancerecord}
                      sx={inputStyle}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button fullWidth variant='contained' type='submit'>
                  Register
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Grid>
  );
};

export default Register;
